import Button from '@app-studio-web/ui/components/Button'
import PageLayout from '@app-studio-web/ui/components/PageLayout'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

interface FeedbackPageProps {
  /** The icon component to display (should be a Heroicon or similar) */
  icon?: ReactNode
  image?: string
  /** Emoji character to display */
  emoji?: string
  /** Translation key for the heading */
  headingKey: string
  /** Translation key for the description/subtitle */
  descriptionKey: string
}

export default function FeedbackPage({
  icon,
  image,
  emoji,
  headingKey,
  descriptionKey,
}: FeedbackPageProps) {
  const submit = useSubmit()
  const { t } = useTranslation()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  return (
    <PageLayout
      background={{
        gradient:
          'linear-gradient(to bottom, #c8d5b9 0%, #ddd5c7 50%, #e8d4c8 100%)',
      }}
    >
      <Form
        className="flex h-full min-h-[calc(100dvh-7rem)] flex-col"
        method="post"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          {/* Icon container with bounce animation */}
          {icon && (
            <div className="animate-bounce-gentle mb-8 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-800">
              {icon}
            </div>
          )}

          {image && (
            <img
              src={image}
              alt="Feedback"
              className="mb-8 h-[64px] w-auto rounded-full"
            />
          )}

          {emoji && <div className="mb-8 text-6xl">{emoji}</div>}

          <h1 className="custom-heading font-headline mb-4 max-w-sm text-3xl leading-tight font-normal text-balance">
            {t(headingKey)}
          </h1>

          <p className="max-w-xs text-sm text-neutral-600">
            {t(descriptionKey)}
          </p>
        </div>

        <div className="mt-8 mb-8">
          <Button
            data-testid="button-continue"
            title={t('shared.continue')}
            type="submit"
          />
        </div>
      </Form>
    </PageLayout>
  )
}
